import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useMemo, useRef } from 'react'
import { fingerprintScheduleEvent } from './fingerprintScheduleEvent'
import { buildScheduleInstanceKey, getScheduleStaffId, type ScheduleInstanceView } from './scheduleInstance'

const fingerprintStaffSlice = (instanceKey: string, events: IPatientSchedule[]): string =>
  `${instanceKey}::${events.map((event) => fingerprintScheduleEvent(event, instanceKey)).join(';')}`

const EMPTY_SCHEDULES: IPatientSchedule[] = []

export const useStaffScheduleSlices = (
  schedules: IPatientSchedule[],
  staff: IPatientConsultationStaff[],
  view: ScheduleInstanceView,
  date: string,
): Map<number, IPatientSchedule[]> => {
  const previousRef = useRef<Map<number, IPatientSchedule[]>>(new Map())

  return useMemo(() => {
    const grouped = new Map<number, IPatientSchedule[]>()
    for (const member of staff) {
      grouped.set(member.id, [])
    }
    for (const event of schedules) {
      const staffId = getScheduleStaffId(event)
      const bucket = grouped.get(staffId)
      if (bucket) bucket.push(event)
    }

    const next = new Map<number, IPatientSchedule[]>()
    for (const member of staff) {
      const instanceKey = buildScheduleInstanceKey({ view, date, staffId: member.id })
      const items = grouped.get(member.id) ?? EMPTY_SCHEDULES
      const previous = previousRef.current.get(member.id)
      if (
        previous &&
        fingerprintStaffSlice(instanceKey, previous) === fingerprintStaffSlice(instanceKey, items)
      ) {
        next.set(member.id, previous)
      } else {
        next.set(member.id, items)
      }
    }

    previousRef.current = next
    return next
  }, [schedules, staff, view, date])
}
